import React, {useState, useEffect, Suspense, lazy, useMemo} from 'react';
import {useHistory} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {
    Box,
    Grid,
    Button,
    TextField,
    Typography,
    FormControl,
    CircularProgress,
} from '@material-ui/core';
import {IconButton} from '@material-ui/core';
import {Clear as ClearIcon} from '@material-ui/icons';
import useQuery from '@pluto/query-hook';
import {setQuery} from '@pluto/set-history-query';
import {getInitialPage} from '@pluto/table';
import {getOptsFromQuery as fromQuery} from '@pluto/select';
import {setMetadataCategoriesAction as getCategories} from '@pluto/metadata-categories/Redux/CategoriesActions';
import {setContentPartnersAction as getContentPartners} from '@pluto/content-partners';
import {setQcGenres as getGenres} from '@pluto/genres/Redux/GenresActions';
import {baseRoutes} from 'App/AppRouter/routes';
import {setSearchParams} from 'QCWorkflow/QCManager/QCManagerContainer';
import {
    setSearchItemsAction as getSearchItems,
    resetSearchItems,
} from './Redux/SearchActions';
import {
    selectContentPartnersData,
    selectContentTypesData,
    selectLabPartnersData,
    selectGenresData,
    selectMetaLangData,
    selectRatingData,
    selectCategoriesData,
    selectRegionsData,
    selectStatusesData,
} from './Redux/SearchSelectors';
import {
    onReset,
    onSearch,
    onFilter,
    handleMount,
    handlePaging,
    onFilterReset,
    handleRequest,
    setInputValue,
    handleUnmount,
    handleKeyDown,
    handleResetSearch,
    handleResetFilters,
    getFilterTitle as getTitle,
} from './SearchHandlers';
import SearchTable from './SearchTable';
import {
    FilterTypes as Types,
    FilterLabels as Labels,
    OptionsDataType,
} from './SearchTypes';
import './Search.scss';

const SearchGroup = lazy(() => import('./SearchGroup/SearchGroupContainer'));

const route = baseRoutes.contentItemsSearch;
const initialRowsPerPage = 25;

export default function SearchContainer() {
    const query = useQuery();
    const history = useHistory();
    const dispatch = useDispatch();
    const initialSearch = query.get(Types.search) || '';
    const [search, setSearchValue] = useState(initialSearch);
    const [searchQuery, setSearchQuery] = useState(initialSearch);
    const [clearFiltersInputs, setClearFiltersInputs] = useState(false);

    const [contType, setContentTypes] = useState<string[]>(
        fromQuery(query, Types.contType)
    );
    const [contPartner, setContentPartners] = useState<string[]>(
        fromQuery(query, Types.contPartner)
    );
    const [labPartner, setLabPartners] = useState<string[]>(
        fromQuery(query, Types.labPartner)
    );
    const [lang, setLang] = useState<string[]>(fromQuery(query, Types.lang));
    const [genre, setGenres] = useState<string[]>(fromQuery(query, Types.genre));
    const [rating, setRatings] = useState<string[]>(fromQuery(query, Types.rating));
    const [category, setCategories] = useState<string[]>(
        fromQuery(query, Types.category)
    );
    const [region, setRegions] = useState<string[]>(fromQuery(query, Types.region));
    const [status, setStatus] = useState<string[]>(fromQuery(query, Types.status));

    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const initialPage = getInitialPage({
        query,
        rowsPerPage,
    });
    const [page, setPage] = useState(initialPage);

    const contentPartnersData = useSelector(selectContentPartnersData);
    const contentTypesData = useSelector(selectContentTypesData);
    const labPartnersData = useSelector(selectLabPartnersData);
    const genresData = useSelector(selectGenresData);
    const langData = useSelector(selectMetaLangData);
    const ratingsData = useSelector(selectRatingData);
    const categoriesData = useSelector(selectCategoriesData);
    const regionsData = useSelector(selectRegionsData);
    const statusData = useSelector(selectStatusesData);

    const filters = useMemo(
        () => ({
            contType,
            contPartner,
            labPartner,
            genre,
            lang,
            rating,
            category,
            region,
            status,
        }),
        [contType, contPartner, labPartner, genre, lang, rating, category, region, status]
    );

    const filtersSetters = [
        setContentTypes,
        setContentPartners,
        setLabPartners,
        setGenres,
        setLang,
        setRatings,
        setCategories,
        setRegions,
        setStatus,
    ];

    const onMount = handleMount({
        search,
        filters,
        dispatch,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        getGenres,
        getCategories,
        getSearchItems,
        getContentPartners,
        genresLoading: genresData.isLoading,
        contentPartnersLoading: contentPartnersData.isLoading,
        categoriesLoading: categoriesData.isLoading,
    });

    useEffect(onMount, []);

    const onUnmount = handleUnmount(dispatch, resetSearchItems);

    useEffect(onUnmount, []);

    useEffect(setSearchParams(window.location.search.replace('?', '')));

    const onRequest = useMemo(
        () =>
            handleRequest({
                route,
                history,
                dispatch,
                setQuery,
                getSearchItems,
            }),
        [setQuery, history, getSearchItems, route]
    );

    const onPaging = useMemo(
        () =>
            handlePaging({
                search: searchQuery,
                onRequest,
                ...filters,
            }),
        [searchQuery, onRequest, filters]
    );

    const handleSearch = onSearch({
        search,
        onRequest,
        rowsPerPage,
        setSearchQuery,
        filters,
    });

    const resetFilters = handleResetFilters({
        clearFiltersInputs,
        setClearFiltersInputs,
        filtersSetters,
    });

    const resetSearch = handleResetSearch(setSearchValue);

    const handleReset = onReset({
        limit: rowsPerPage,
        search: searchQuery,
        route,
        setPage,
        history,
        dispatch,
        setQuery,
        resetFilters,
        getSearchItems,
    });

    const handleSearchChange = setInputValue(setSearchValue);

    const filterParams = useMemo(
        () => ({
            page,
            onRequest,
            rowsPerPage,
            search,
            filters,
            setSearchQuery,
        }),
        [search, page, onRequest, rowsPerPage, filters]
    );

    const onKeyDown = handleKeyDown(handleSearch);

    const contentTypesProps = useMemo(
        () => ({
            optionsData: contentTypesData as OptionsDataType,
            selectedValues: filterParams.filters.contType,
            onChange: onFilter(filterParams, setContentTypes, Types.contType),
            resetFilter: onFilterReset(filterParams, setContentTypes, Types.contType),
        }),
        [contentTypesData, filterParams]
    );

    const contentPartnersProps = useMemo(
        () => ({
            optionsData: contentPartnersData as OptionsDataType,
            selectedValues: filterParams.filters.contPartner,
            onChange: onFilter(filterParams, setContentPartners, Types.contPartner),
            clearFilter: clearFiltersInputs,
            resetFilter: onFilterReset(
                filterParams,
                setContentPartners,
                Types.contPartner
            ),
        }),
        [contentPartnersData, filterParams, clearFiltersInputs]
    );

    const labPartnersProps = useMemo(
        () => ({
            optionsData: labPartnersData as OptionsDataType,
            selectedValues: filterParams.filters.labPartner,
            onChange: onFilter(filterParams, setLabPartners, Types.labPartner),
            resetFilter: onFilterReset(filterParams, setLabPartners, Types.labPartner),
        }),
        [labPartnersData, filterParams]
    );

    const genresProps = useMemo(
        () => ({
            optionsData: genresData as OptionsDataType,
            selectedValues: filterParams.filters.genre,
            onChange: onFilter(filterParams, setGenres, Types.genre),
            resetFilter: onFilterReset(filterParams, setGenres, Types.genre),
        }),
        [genresData, filterParams]
    );

    const langProps = useMemo(
        () => ({
            optionsData: langData as OptionsDataType,
            selectedValues: filterParams.filters.lang,
            onChange: onFilter(filterParams, setLang, Types.lang),
            resetFilter: onFilterReset(filterParams, setLang, Types.lang),
        }),
        [langData, filterParams]
    );

    const ratingsProps = useMemo(
        () => ({
            optionsData: ratingsData as OptionsDataType,
            selectedValues: filterParams.filters.rating,
            onChange: onFilter(filterParams, setRatings, Types.rating),
            resetFilter: onFilterReset(filterParams, setRatings, Types.rating),
        }),
        [ratingsData, filterParams]
    );

    const categoriesProps = useMemo(
        () => ({
            optionsData: categoriesData as OptionsDataType,
            selectedValues: filterParams.filters.category,
            onChange: onFilter(filterParams, setCategories, Types.category),
            resetFilter: onFilterReset(filterParams, setCategories, Types.category),
        }),
        [categoriesData, filterParams]
    );

    const regionsProps = useMemo(
        () => ({
            optionsData: regionsData as OptionsDataType,
            selectedValues: filterParams.filters.region,
            onChange: onFilter(filterParams, setRegions, Types.region),
            resetFilter: onFilterReset(filterParams, setRegions, Types.region),
        }),
        [regionsData, filterParams]
    );

    const statusProps = useMemo(
        () => ({
            optionsData: statusData as OptionsDataType,
            selectedValues: filterParams.filters.status,
            onChange: onFilter(filterParams, setStatus, Types.status),
            resetFilter: onFilterReset(filterParams, setStatus, Types.status),
        }),
        [statusData, filterParams]
    );

    return (
        <Box className="search">
            <Suspense fallback={<CircularProgress />}>
                <Grid container spacing={1}>
                    <Grid item xs={6} sm={5} md={4} lg={3} xl={2}>
                        <div className="search-filters">
                            <FormControl className="search-input">
                                <TextField
                                    id="search"
                                    autoComplete="off"
                                    variant="filled"
                                    label="Search by title or ID..."
                                    size="small"
                                    value={search}
                                    onChange={handleSearchChange}
                                    onKeyDown={onKeyDown}
                                    InputProps={{
                                        endAdornment: search && (
                                            <IconButton
                                                aria-label="clear search input"
                                                onClick={resetSearch}
                                            >
                                                <ClearIcon className="clear" />
                                            </IconButton>
                                        ),
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    className="search-button"
                                    onClick={handleSearch}
                                >
                                    Search
                                </Button>
                            </FormControl>
                            <Box className="search-filters-title">
                                <Typography variant="h6">Filters</Typography>
                                <Button
                                    className="text-primary-button"
                                    onClick={handleReset}
                                >
                                    Clear all
                                </Button>
                            </Box>
                            <Box className="search-filters-container">
                                <SearchGroup
                                    title={getTitle(Labels.contType, contType)}
                                    groupState={contentTypesProps}
                                    minHeight={130}
                                />
                                <SearchGroup
                                    title={getTitle(Labels.contPartner, contPartner)}
                                    groupState={contentPartnersProps}
                                    filterable
                                />
                                <SearchGroup
                                    title={getTitle(Labels.labPartner, labPartner)}
                                    groupState={labPartnersProps}
                                />
                                <SearchGroup
                                    title={getTitle(Labels.lang, lang)}
                                    groupState={langProps}
                                    filterable
                                />
                                <SearchGroup
                                    title={getTitle(Labels.genre, genre)}
                                    groupState={genresProps}
                                />
                                <SearchGroup
                                    title={getTitle(Labels.rating, rating)}
                                    groupState={ratingsProps}
                                    filterable
                                />
                                <SearchGroup
                                    title={getTitle(Labels.category, category)}
                                    groupState={categoriesProps}
                                />
                                <SearchGroup
                                    title={getTitle(Labels.region, region)}
                                    groupState={regionsProps}
                                    filterable
                                />
                                <SearchGroup
                                    title={getTitle(Labels.status, status)}
                                    groupState={statusProps}
                                />
                            </Box>
                        </div>
                    </Grid>
                    <Grid item xs={6} sm={7} md={8} lg={9} xl={10}>
                        <SearchTable
                            searchQuery={searchQuery}
                            page={page}
                            setPage={setPage}
                            rowsPerPage={rowsPerPage}
                            setRowsPerPage={setRowsPerPage}
                            onPaging={onPaging}
                        />
                    </Grid>
                </Grid>
            </Suspense>
        </Box>
    );
}
